import React, { useEffect, useState, useCallback } from 'react';
import { Plus, ChevronDown, ChevronUp, RefreshCw, Trash2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { toast } from '../../components/ui/sonner';
import api from '../../services/api';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n || 0);
const inputCls = 'border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-900';
const ENTITIES = ['Eyvo USA', 'Eyvo India'];
const ENTITY_MAP = { usa: 'Eyvo USA', india: 'Eyvo India' };

const emptyLine = () => ({ account_id: '', description: '', debit: '', credit: '' });

export default function JournalEntries() {
  const location = useLocation();
  const { pathname, state: navState } = location;
  const entitySlug = pathname.split('/')[2];
  const lockedEntity = ENTITY_MAP[entitySlug] || null;
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: '', description: '', entity: lockedEntity || 'Eyvo USA', reference: '', lines: [emptyLine(), emptyLine()] });
  const [entity, setEntity] = useState(lockedEntity || '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (entity) params.set('entity', entity);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    try {
      const [jRes, aRes] = await Promise.all([
        api.get(`/api/accounts/journal-entries?${params}`),
        api.get('/api/accounts/chart'),
      ]);
      setEntries(jRes.data);
      setAccounts(aRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // Pre-fill form from navigation state (e.g. "Correct split" shortcut from Xero Import page)
  useEffect(() => {
    if (navState?.prefill) {
      const { date, description, reference, lines } = navState.prefill;
      setForm(f => ({
        ...f,
        date:        date        || f.date,
        description: description || f.description,
        reference:   reference   || f.reference,
        lines: lines?.length ? lines : f.lines,
      }));
      setShowForm(true);
      toast.info('Form pre-filled from split correction — enter the correct split amounts and save.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accountMap = Object.fromEntries(accounts.map(a => [a.account_id, a]));
  const totalDebit = form.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const updateLine = (i, field, val) =>
    setForm(f => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [field]: val } : l) }));

  const save = async () => {
    if (!form.date || !form.description) { toast.error('Date and description required'); return; }
    if (!isBalanced) { toast.error(`Journal not balanced. Debits: ${fmt(totalDebit)}, Credits: ${fmt(totalCredit)}`); return; }
    const lines = form.lines.filter(l => l.account_id);
    if (lines.length < 2) { toast.error('Need at least 2 lines'); return; }
    setSaving(true);
    try {
      await api.post('/api/accounts/journal-entries', {
        ...form,
        lines: lines.map(l => ({
          account_id: l.account_id,
          description: l.description || undefined,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
        })),
      });
      setShowForm(false);
      setForm({ date: '', description: '', entity: 'Eyvo USA', reference: '', lines: [emptyLine(), emptyLine()] });
      toast.success('Journal entry posted successfully');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to post journal entry'); }
    setSaving(false);
  };

  return (
    <div className="p-6 fade-in" data-testid="journal-entries-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Journal Entries</h1>
          <p className="text-sm text-slate-500 mt-0.5">Double-entry bookkeeping ledger</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 border border-slate-200 rounded-md hover:bg-slate-50">
            <RefreshCw size={14} className="text-slate-500" />
          </button>
          <button onClick={() => setShowForm(p => !p)}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-indigo-900 text-white text-sm font-medium rounded-md hover:bg-indigo-800"
            data-testid="new-journal-entry-btn">
            <Plus size={14} /> New Entry
          </button>
        </div>
      </div>

      {/* New Entry Form */}
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 mb-5" data-testid="journal-entry-form">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">New Journal Entry</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Date *</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-slate-500 mb-1 block">Description *</label>
              <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="e.g. Monthly payroll" className={inputCls + ' w-full'} />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Entity</label>
              <select value={form.entity} onChange={e => setForm(f => ({ ...f, entity: e.target.value }))} className={inputCls + ' w-full'}>
                {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
          </div>

          {/* Lines */}
          <div className="border border-slate-200 rounded-lg overflow-hidden mb-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">Account</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">Description</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500">Debit</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500">Credit</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {form.lines.map((line, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 min-w-56">
                      <select value={line.account_id} onChange={e => updateLine(i, 'account_id', e.target.value)}
                        className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-900">
                        <option value="">— Select —</option>
                        {accounts.map(a => (
                          <option key={a.account_id} value={a.account_id}>{a.account_code} {a.account_name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input value={line.description} onChange={e => updateLine(i, 'description', e.target.value)}
                        placeholder="Optional" className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-900" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" step="0.01" value={line.debit} onChange={e => updateLine(i, 'debit', e.target.value)}
                        className="w-full text-xs text-right border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-900" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" step="0.01" value={line.credit} onChange={e => updateLine(i, 'credit', e.target.value)}
                        className="w-full text-xs text-right border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-900" />
                    </td>
                    <td className="px-3 py-2">
                      {form.lines.length > 2 && (
                        <button onClick={() => removeLine(i)} className="text-slate-300 hover:text-red-500">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t border-slate-200">
                  <td colSpan={2} className="px-3 py-2">
                    <button onClick={addLine} className="text-xs text-indigo-700 hover:text-indigo-900 flex items-center gap-1">
                      <Plus size={12} /> Add Line
                    </button>
                  </td>
                  <td className={`px-3 py-2 text-right font-mono text-xs font-semibold ${isBalanced ? 'text-emerald-700' : 'text-red-600'}`}>
                    {fmt(totalDebit)}
                  </td>
                  <td className={`px-3 py-2 text-right font-mono text-xs font-semibold ${isBalanced ? 'text-emerald-700' : 'text-red-600'}`}>
                    {fmt(totalCredit)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {!isBalanced && totalDebit > 0 && (
            <p className="text-xs text-red-600 mb-3">
              Journal is not balanced. Difference: {fmt(Math.abs(totalDebit - totalCredit))}
            </p>
          )}

          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving || !isBalanced}
              className="px-4 py-2 bg-indigo-900 text-white text-xs rounded hover:bg-indigo-800 disabled:opacity-50"
              data-testid="save-journal-entry-btn">
              {saving ? 'Posting…' : 'Post Entry'}
            </button>
            <button onClick={() => setShowForm(false)} className="text-xs text-slate-500 px-2">Cancel</button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <select value={entity} onChange={e => setEntity(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none">
          <option value="">All Entities</option>
          {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none" />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none" />
      </div>

      {/* Entries List */}
      <div className="space-y-2" data-testid="journal-entries-list">
        {loading ? (
          [...Array(5)].map((_, i) => <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse" />)
        ) : entries.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg px-5 py-10 text-center text-slate-400 text-sm">
            No journal entries found. Create your first entry above.
          </div>
        ) : entries.map(entry => (
          <div key={entry.journal_id} className="bg-white border border-slate-200 rounded-lg overflow-hidden"
            data-testid={`journal-entry-${entry.journal_id}`}>
            <button
              onClick={() => setExpanded(e => e === entry.journal_id ? null : entry.journal_id)}
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-4 text-left">
                <span className="font-mono text-xs text-slate-400 w-24 flex-shrink-0">{entry.date}</span>
                <div>
                  <p className="text-sm font-medium text-slate-800">{entry.description}</p>
                  <p className="text-xs text-slate-400">{entry.entity} · {entry.source || 'manual'}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${entry.is_balanced ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                  {entry.is_balanced ? 'Balanced' : 'Unbalanced'}
                </span>
                <span className="font-mono text-sm font-semibold text-slate-700">{fmt(entry.total_debit)}</span>
                {expanded === entry.journal_id ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
              </div>
            </button>
            {expanded === entry.journal_id && (
              <div className="border-t border-slate-100">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="text-left px-5 py-2 font-semibold text-slate-500">Account</th>
                      <th className="text-left px-5 py-2 font-semibold text-slate-500">Description</th>
                      <th className="text-right px-5 py-2 font-semibold text-slate-500">Debit</th>
                      <th className="text-right px-5 py-2 font-semibold text-slate-500">Credit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {(entry.lines || []).map((line, i) => (
                      <tr key={i} className="hover:bg-slate-50/50">
                        <td className="px-5 py-2">
                          <span className="font-mono text-slate-400 mr-2">{line.account_code}</span>
                          <span className="text-slate-700">{line.account_name}</span>
                        </td>
                        <td className="px-5 py-2 text-slate-400">{line.description || '—'}</td>
                        <td className="px-5 py-2 text-right font-mono text-slate-700">{line.debit > 0 ? fmt(line.debit) : ''}</td>
                        <td className="px-5 py-2 text-right font-mono text-slate-700">{line.credit > 0 ? fmt(line.credit) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
